import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Service to manage footer sticky modals height
 * Calculates and provides the total height of all sticky modals at the bottom of the page
 */
@Injectable({
  providedIn: 'root',
})
export class FooterStickyModalService {
  private heightSubject$ = new BehaviorSubject<number>(0);
  private modalElements = new Map<string, { element: HTMLElement; observer: ResizeObserver }>();

  /**
   * Observable that emits the current total height of all sticky modals
   */
  get height$(): Observable<number> {
    return this.heightSubject$.asObservable();
  }

  /**
   * Current total height of all sticky modals
   */
  get currentHeight(): number {
    return this.heightSubject$.value;
  }

  /**
   * Register a modal element to track its height
   * @param id Unique identifier for the modal
   * @param element HTML element of the modal
   */
  registerModal(id: string, element: HTMLElement): void {
    // Don't register if already exists
    if (this.modalElements.has(id)) {
      return;
    }

    // Create ResizeObserver to watch for height changes
    const observer = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure calculation happens after render
      requestAnimationFrame(() => {
        this.calculateTotalHeight();
      });
    });

    observer.observe(element);

    this.modalElements.set(id, { element, observer });

    // Initial calculation with a small delay to ensure element is fully rendered
    setTimeout(() => {
      this.calculateTotalHeight();
    }, 0);
  }

  /**
   * Unregister a modal element
   * @param id Unique identifier for the modal
   */
  unregisterModal(id: string): void {
    const modalData = this.modalElements.get(id);

    if (modalData) {
      modalData.observer.disconnect();
      this.modalElements.delete(id);
      this.calculateTotalHeight();
    }
  }

  /**
   * Calculate and emit the total height of all registered modals
   */
  private calculateTotalHeight(): void {
    let totalHeight = 0;

    this.modalElements.forEach(({ element }) => {
      // Check if element is visible in the DOM
      const isVisible = element.offsetParent !== null || element.offsetHeight > 0;

      if (isVisible) {
        // Use getBoundingClientRect for more accurate height
        const rect = element.getBoundingClientRect();
        const elementHeight = rect.height || element.offsetHeight;
        totalHeight = Math.max(totalHeight, elementHeight);
      }
    });

    // Only emit if value changed to avoid unnecessary updates
    if (this.heightSubject$.value !== totalHeight) {
      this.heightSubject$.next(totalHeight);
    }
  }
}
